import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowDown, ArrowUp } from 'lucide-react';

import { useFinancialOverview } from '@/composables/financial/useFinancialOverview';
import { PageContainer } from '@/components/layout/PageContainer';
import { OverviewMonthChart } from '@/components/financial/OverviewMonthChart';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/cn';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { daysUntil, dueLabel, expensesOf, incomeOf, monthLong, monthShortYear, totalsByCurrency } from '@/lib/financialOverview';

export function FinancialOverviewPage() {
  const o = useFinancialOverview();
  const money = (value: number) => formatCurrency(value, o.currency);
  const s = o.summary;

  return (
    <PageContainer
      title="Financial Overview"
      description={o.periodLabel}
      actions={
        <div className="space-y-1">
          <Label htmlFor="overview-fy" className="text-xs text-muted-foreground">Financial year</Label>
          <Select value={o.fy} onValueChange={o.setFy}>
            <SelectTrigger id="overview-fy" className="w-60"><SelectValue /></SelectTrigger>
            <SelectContent>{o.yearOptions.map((y) => <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Month">
          <Button size="sm" variant={o.month ? 'outline' : 'default'} aria-pressed={!o.month} onClick={() => o.setMonth(null)}>
            {o.fy === 'all' ? 'All months' : 'Whole year'}
          </Button>
          <span aria-hidden="true" className="mx-1.5 h-5 w-px bg-border" />
          {o.slots.map((slot) => (
            <Button
              key={slot.month}
              size="sm"
              className="min-w-11 px-2.5"
              variant={o.month === slot.month ? 'default' : 'outline'}
              disabled={!slot.data}
              aria-pressed={o.month === slot.month}
              aria-label={monthLong(slot.month)}
              onClick={() => o.setMonth(o.month === slot.month ? null : slot.month)}
            >
              {slot.label}
            </Button>
          ))}
        </div>

        {o.isError ? (
          <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Could not load the overview. Refresh the page to try again.</CardContent></Card>
        ) : o.isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}</div>
        ) : !s ? (
          <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No ledger entries in this period.</CardContent></Card>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Stat
                label={<><Swatch className="bg-blue-600 dark:bg-blue-500" />Income received</>}
                value={money(s.income)}
                note={`Clients ${money(s.clientIncome)} · Interest & other ${money(s.otherIncome)}`}
              />
              <Stat
                label={<><Swatch className="bg-orange-600" />Expenses paid</>}
                value={money(s.expenses)}
                note={s.refunds > 0 ? `After ${money(s.refunds)} of refunds` : s.categories[0] ? `Largest: ${s.categories[0].name} ${money(s.categories[0].amount)}` : 'No expenses in this period'}
              />
              <Stat
                label={s.result >= 0
                  ? <><ArrowUp aria-hidden="true" className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />Surplus</>
                  : <><ArrowDown aria-hidden="true" className="h-3.5 w-3.5 text-orange-600" />Shortfall</>}
                value={money(s.result)}
                note="Income minus expenses"
              />
              <Stat
                label={<>Cash at end of {o.lastMonth ? monthShortYear(o.lastMonth) : ''}</>}
                value={money(s.closingBalance)}
                note={`Started the period with ${money(s.openingBalance)}`}
              />
            </div>

            <div className="flex flex-wrap items-stretch gap-4">
              <Card className="min-w-0 flex-[999_1_560px]">
                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 space-y-0">
                  <div>
                    <CardTitle className="text-base">Month by month</CardTitle>
                    <p className="mt-0.5 text-xs text-muted-foreground">Click a month to filter the whole page</p>
                  </div>
                  <div className="flex gap-3.5 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Swatch className="bg-blue-600 dark:bg-blue-500" />Income</span>
                    <span className="flex items-center gap-1.5"><Swatch className="bg-orange-600" />Expenses</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <OverviewMonthChart slots={o.slots} selected={o.month} currency={o.currency} onSelect={o.setMonth} />
                  <div className="overflow-x-auto border-t pt-1">
                    <Table className="min-w-[620px] text-[13px] tabular-nums">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>
                          <TableHead className="text-right">Income</TableHead>
                          <TableHead className="text-right">Expenses</TableHead>
                          <TableHead className="text-right">Surplus / shortfall</TableHead>
                          <TableHead className="text-right">Month-end cash</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {o.inYear.map((m) => (
                          <TableRow key={m.month} className={cn(o.month === m.month && 'bg-primary/10')}>
                            <TableCell className="py-0">
                              <button type="button" className="min-h-10 font-medium hover:underline" onClick={() => o.setMonth(o.month === m.month ? null : m.month)}>
                                {monthLong(m.month)}
                              </button>
                            </TableCell>
                            <TableCell className="py-0 text-right">{money(incomeOf(m))}</TableCell>
                            <TableCell className="py-0 text-right">{money(expensesOf(m))}</TableCell>
                            <TableCell className="py-0 text-right">{money(incomeOf(m) - expensesOf(m))}</TableCell>
                            <TableCell className="py-0 text-right">{money(m.closingBalance)}</TableCell>
                          </TableRow>
                        ))}
                        <TotalRow months={o.inYear} money={money} />
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              <Card className="min-w-0 flex-[1_1_320px]">
                <CardHeader>
                  <CardTitle className="text-base">How the cash moved</CardTitle>
                  <p className="text-xs text-muted-foreground">{o.periodShort}</p>
                </CardHeader>
                <CardContent className="space-y-3 text-[13px] tabular-nums">
                  <div>
                    <Line label="Opening balance" value={money(s.openingBalance)} strong />
                    <Line label="From clients" value={`+ ${money(s.clientIncome)}`} />
                    <Line label="Interest & other income" value={`+ ${money(s.otherIncome)}`} />
                    {s.refunds > 0 && <Line label="Refunds received" value={`+ ${money(s.refunds)}`} />}
                    <Line label="Expenses" value={`− ${money(s.grossExpenses)}`} />
                  </div>
                  {s.nonOperating.length > 0 && (
                    <div className="rounded-lg bg-muted/60 p-3">
                      <p className="mb-1 text-xs font-semibold text-muted-foreground">Not income or expense</p>
                      {s.nonOperating.map((row, i) => (
                        <div key={`${row.label}-${i}`} className="flex justify-between gap-3 py-1">
                          <span className="text-muted-foreground">{row.label}</span>
                          <span className="whitespace-nowrap">{row.amount < 0 ? '−' : '+'} {money(Math.abs(row.amount))}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between gap-3 border-t border-foreground/30 pt-2.5 text-sm font-bold">
                    <span>Closing balance</span>
                    <span>{money(s.closingBalance)}</span>
                  </div>
                  <p className="font-sans text-xs text-muted-foreground">
                    Cash includes money in the bank's sweep FD, so it reads higher than the current-account figure on the statement.
                    {o.ledgerThrough && ` Latest ledger entry: ${formatDate(o.ledgerThrough)}.`}
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <RankedCard title="Who paid us" period={o.periodShort} rows={s.clients} money={money} barClass="bg-blue-600 dark:bg-blue-500" empty="No client payments in this period." />
              <RankedCard title="Where the money went" period={o.periodShort} rows={s.categories} money={money} barClass="bg-orange-600" empty="No expenses in this period." />
            </div>
          </>
        )}

        {!o.isLoading && !o.isError && (
          <div className="grid gap-4 lg:grid-cols-2">
            <DocsCard
              title="Still to collect"
              subtitle="Unpaid invoices, as of today"
              totals={totalsByCurrency(o.unpaidInvoices, (u) => u.amount)}
              empty="Every invoice is paid."
            >
              {o.unpaidInvoices.map((u) => {
                const days = daysUntil(u.dueDate);
                return (
                  <DocRow
                    key={u.id}
                    client={u.client}
                    amount={formatCurrency(u.amount, u.currency)}
                    href={`/financial/invoices/${u.id}`}
                    number={u.number}
                    detail={
                      <span className={cn('flex items-center justify-end gap-1', days < 0 ? 'font-semibold text-red-700 dark:text-red-400' : 'text-muted-foreground')}>
                        {days < 0 && <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5" />}
                        {dueLabel(days)}
                      </span>
                    }
                  />
                );
              })}
            </DocsCard>
            <DocsCard
              title="Agreed, not yet invoiced"
              subtitle="Left to bill on accepted quotes, as of today"
              totals={totalsByCurrency(o.unbilledQuotes, (q) => q.left)}
              empty="Accepted quotes are fully invoiced."
            >
              {o.unbilledQuotes.map((q) => (
                <DocRow
                  key={q.id}
                  client={q.client}
                  amount={formatCurrency(q.left, q.currency)}
                  href={`/financial/quotations/${q.id}`}
                  number={q.number}
                  detail={<span className="block text-right tabular-nums text-muted-foreground">of {formatCurrency(q.total, q.currency)}</span>}
                />
              ))}
            </DocsCard>
          </div>
        )}
      </div>
    </PageContainer>
  );
}

function Swatch({ className }: { className: string }) {
  return <span aria-hidden="true" className={cn('h-2.5 w-2.5 rounded-[3px]', className)} />;
}

function Stat({ label, value, note }: { label: React.ReactNode; value: string; note: string }) {
  return (
    <Card>
      <CardContent className="space-y-2 p-5">
        <div className="flex items-center gap-2 text-[13px] font-medium text-muted-foreground">{label}</div>
        <div className="text-2xl font-bold tracking-tight tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground">{note}</div>
      </CardContent>
    </Card>
  );
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between gap-3 border-t py-2 first:border-t-0', strong && 'font-semibold')}>
      <span className={cn(!strong && 'text-muted-foreground')}>{label}</span>
      <span className="whitespace-nowrap">{value}</span>
    </div>
  );
}

function TotalRow({ months, money }: { months: Parameters<typeof incomeOf>[0][]; money: (v: number) => string }) {
  const income = months.reduce((sum, m) => sum + incomeOf(m), 0);
  const expenses = months.reduce((sum, m) => sum + expensesOf(m), 0);
  return (
    <TableRow className="font-semibold hover:bg-transparent">
      <TableCell>Total</TableCell>
      <TableCell className="text-right">{money(income)}</TableCell>
      <TableCell className="text-right">{money(expenses)}</TableCell>
      <TableCell className="text-right">{money(income - expenses)}</TableCell>
      <TableCell className="text-right">{months.length ? money(months[months.length - 1].closingBalance) : ''}</TableCell>
    </TableRow>
  );
}

function RankedCard({ title, period, rows, money, barClass, empty }: {
  title: string;
  period: string;
  rows: Array<{ name: string; amount: number }>;
  money: (v: number) => string;
  barClass: string;
  empty: string;
}) {
  const total = rows.reduce((sum, r) => sum + r.amount, 0);
  const peak = rows[0]?.amount || 1;
  return (
    <Card>
      <CardHeader className="flex flex-row items-baseline justify-between gap-3 space-y-0">
        <CardTitle className="text-base">{title}</CardTitle>
        <span className="text-xs text-muted-foreground">{period}</span>
      </CardHeader>
      <CardContent className="space-y-3.5">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">{empty}</p>}
        {rows.map((r) => (
          <div key={r.name} className="space-y-1.5">
            <div className="flex justify-between gap-3 text-[13px]">
              <span className="min-w-0 break-words">{r.name}</span>
              <span className="flex-none tabular-nums text-muted-foreground">{money(r.amount)} · {Math.max(1, Math.round((r.amount / total) * 100))}%</span>
            </div>
            <div aria-hidden="true" className="h-2 rounded bg-muted">
              <div className={cn('h-2 rounded', barClass)} style={{ width: `${Math.max(1, Math.round((r.amount / peak) * 100))}%` }} />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function DocsCard({ title, subtitle, totals, empty, children }: {
  title: string;
  subtitle: string;
  totals: Array<{ currency: string; amount: number }>;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-baseline justify-between gap-x-3 gap-y-1 space-y-0">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {totals.length > 0 && (
          <div className="text-[15px] font-bold tabular-nums">{totals.map((t) => formatCurrency(t.amount, t.currency)).join(' + ')}</div>
        )}
      </CardHeader>
      <CardContent className="text-[13px]">
        {totals.length === 0 ? <p className="text-sm text-muted-foreground">{empty}</p> : children}
      </CardContent>
    </Card>
  );
}

function DocRow({ client, amount, href, number, detail }: { client: string; amount: string; href: string; number: string; detail: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 border-t py-2.5">
      <span className="min-w-0 font-medium">{client}</span>
      <span className="text-right font-semibold tabular-nums">{amount}</span>
      <Link to={href} className="text-xs text-primary hover:underline">{number}</Link>
      <span className="text-xs">{detail}</span>
    </div>
  );
}
